"use client";

import { useState, useEffect } from 'react';
import { Box, Tabs, Tab, Container, Paper, Typography } from '@mui/material';
import NewProjectList from '@/components/projects/NewProjectList.tsx';
import UserManagement from '@/components/home/UserManagement.tsx';
import PlansAdmin from '@/components/PlansAdmin.tsx';
import PerformanceTargetForm from '@/components/home/PerformanceTargetForm.tsx';
import MonitoringForm from '@/components/home/MonitoringForm.tsx';
import ArticleIcon from '@mui/icons-material/Article';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import HandshakeIcon from '@mui/icons-material/Handshake';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
  [key: string]: any;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`main-tabpanel-${index}`}
      aria-labelledby={`main-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function getA11yProps(index: number) {
  return {
    id: `main-tab-${index}`,
    "aria-controls": `main-tabpanel-${index}`,
  };
}

// 占位符组件
interface PlaceholderComponentProps {
  title: string;
  description: string;
}

function PlaceholderComponent({
  title,
  description,
}: PlaceholderComponentProps) {
  return (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <Typography variant="h5" gutterBottom>
        {title}
      </Typography>
      <Typography variant="body1" color="text.secondary">
        {description}
      </Typography>
    </Box>
  );
}

export default function HomePage() {
  const [value, setValue] = useState(0);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    // 防止点击图标/空白被阻断，强制更新索引
    setValue(Number(newValue));
  };

  // 获取当前用户角色以决定是否显示用户管理标签页
  let currentUserRole = null;
  if (isClient) {
    // 确保 localStorage 只在客户端被访问
    try {
      const userStr = localStorage.getItem("currentUser");
      console.log("MainTabs - localStorage currentUser:", userStr); // 添加调试日志
      if (userStr) {
        const userObj = JSON.parse(userStr);
        console.log("MainTabs - parsed user object:", userObj); // 添加调试日志
        currentUserRole = userObj.role;
        console.log("MainTabs - extracted role:", currentUserRole); // 添加调试日志
      }
    } catch (e) {
      console.error(
        "Error parsing current user from localStorage for MainTabs:",
        e,
      );
    }
  }

  const isAdmin =
    currentUserRole === "admin" ||
    currentUserRole === "zhaoyuan_admin" ||
    currentUserRole === "super_admin";
  const canManagePlans =
    currentUserRole === "super_admin" || currentUserRole === "zhaoyuan_admin";

  console.log(
    "MainTabs - isAdmin:",
    isAdmin,
    "canManagePlans:",
    canManagePlans,
  ); // 添加调试日志

  // 计算动态索引
  const getUserManagementIndex = () => {
    let index = 5; // 基础索引（前5个标签页：重点项目、部门整体、绩效目标申报表、事前绩效评估、事中监控）
    return index;
  };

  const getPlanManagementIndex = () => {
    let index = 6; // 基础索引
    return index;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Paper elevation={0} sx={{ width: "100%", bgcolor: "transparent" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="main content tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              label="重点项目"
              icon={<ArticleIcon />}
              iconPosition="start"
              {...getA11yProps(0)}
            />
            <Tab
              label="部门整体"
              icon={<CorporateFareIcon />}
              iconPosition="start"
              disabled
            />
            <Tab
              label="绩效目标申报表"
              icon={<AssessmentIcon />}
              iconPosition="start"
              {...getA11yProps(2)}
            />
            <Tab
              label="事前绩效评估"
              icon={<HandshakeIcon />}
              iconPosition="start"
              disabled
            />
            <Tab
              label="事中监控"
              icon={<MonitorHeartIcon />}
              iconPosition="start"
              disabled
              {...getA11yProps(4)}
            />
            {isAdmin && (
              <Tab label="用户管理" icon={<AccountBoxIcon />} iconPosition="start" {...getA11yProps(getUserManagementIndex())} onClick={() => setValue(getUserManagementIndex())} />
            )}
            {canManagePlans && (
              <Tab label="套餐管理" icon={<ListAltIcon />} iconPosition="start" {...getA11yProps(getPlanManagementIndex())} onClick={() => setValue(getPlanManagementIndex())} />
            )}
          </Tabs>
        </Box>
        <TabPanel value={value} index={0}>
          <NewProjectList />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <PlaceholderComponent
            title="部门整体"
            description="部门整体功能正在开发中，敬请期待..."
          />
        </TabPanel>
        <TabPanel value={value} index={2}>
          <PerformanceTargetForm />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <PlaceholderComponent
            title="事前绩效评估"
            description="事前绩效评估功能正在开发中，敬请期待..."
          />
        </TabPanel>
        <TabPanel value={value} index={4}>
          <MonitoringForm />
        </TabPanel>
        {isAdmin && (
          <TabPanel value={value} index={getUserManagementIndex()}>
            <UserManagement />
          </TabPanel>
        )}
        {canManagePlans && (
          <TabPanel value={value} index={getPlanManagementIndex()}>
            <PlansAdmin />
          </TabPanel>
        )}
      </Paper>
    </Container>
  );
}
